import { useCallback, useEffect, useState } from 'react';
import { getMonitorAlarms, type MonitorAlarm, type MonitorAlarmsParams } from '@/services/api';

const ALARM_CHANGED = 'alarm-changed';

/**
 * Una alarma cambió (una acción desde el modal o la página): las listas
 * montadas se vuelven a pedir. Un evento de ventana porque el modal vive en el
 * layout y no conoce a quien lo abrió.
 */
export function notifyAlarmChanged(): void {
  window.dispatchEvent(new CustomEvent(ALARM_CHANGED));
}

export type MonitorAlarmFilters = Omit<MonitorAlarmsParams, 'limit' | 'offset'>;

/**
 * Una página de alarmas (`GET /api/monitor/alarms`), paginada en servidor: las
 * resueltas se acumulan con el tiempo. Gemelo de `useMonitorEvents`.
 *
 * Los filtros se comparan por valor (su JSON), así que quien llama puede pasar
 * un objeto nuevo en cada render. Cambiar de filtros vuelve a la primera página
 * en el mismo render, sin pedir antes la página vieja con los filtros nuevos.
 * `enabled: false` no pide nada (p. ej. mientras falta el taller del ámbito).
 */
export function useMonitorAlarms(filters: MonitorAlarmFilters, pageSize = 50, enabled = true) {
  const key = JSON.stringify(filters);
  const [paging, setPaging] = useState({ key, offset: 0 });
  const offset = paging.key === key ? paging.offset : 0;
  const [page, setPage] = useState<{ rows: MonitorAlarm[]; total: number }>({ rows: [], total: 0 });
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    const parsed = JSON.parse(key) as MonitorAlarmFilters;
    getMonitorAlarms({ ...parsed, limit: pageSize, offset })
      .then((d) => {
        if (!cancelled) setPage({ rows: d.items, total: d.total });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'No se pudieron cargar las alarmas');
        setPage({ rows: [], total: 0 });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, offset, pageSize, tick, enabled]);

  useEffect(() => {
    const onChanged = () => setTick((n) => n + 1);
    window.addEventListener(ALARM_CHANGED, onChanged);
    return () => window.removeEventListener(ALARM_CHANGED, onChanged);
  }, []);

  const setOffset = useCallback((next: number) => setPaging({ key, offset: next }), [key]);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  return { rows: page.rows, total: page.total, offset, pageSize, setOffset, loading, error, reload };
}
